import {
	AppointmentBookingEnum,
	AutoSolEnum,
	ExternalServicesTaggingEnum,
	MyReferralsTaggingEnum,
	NetworkEnum,
	RefundTaggingEnum,
	ServicesTaggingEnum,
} from 'main/analytics/gaTagsEnum';
import {
	makeGetExamResultsUrl,
	makeGetPerksClubLink,
	makeGetPinSsUrl,
	makeGetRefundOdonto,
	makeGetStayLetterUrl,
} from 'main/factories/usecases';

import {
	AuthorizationsEnum,
	ConsultProtocolsEnum,
	EmergencyTelehealthEnum,
	ExamBookingEnum,
	ExamResultsEnum,
	FavoritesEnum,
	FeaturesEnum,
	GuidesEnum,
	HealthBookingEnum,
	HelpCenterEnum,
	MyAppointmentsEnum,
	MyPaymentsEnum,
	MyReferralsEnum,
	OdontoBookingEnum,
	RefundEnum,
	TelehealthBookingEnum,
	TermsOriginEnum,
} from 'presentation/constants';
import {EXAMS_FEATURE_BY_HOST} from 'presentation/constants/examsFeatureByHost';
import {useContractStatus, useHealthEmergencyLinkFactory, useHostName} from 'presentation/hooks';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {translations} from 'presentation/translations-new';
import {findItemByQuery} from '../utils/findItemByQuery';

import {useAccountInformation} from '../useAccountInformation';
import {useConciergeBenefits} from '../useConciergeBenefits';
import {Service, ServicesTypesEnum} from './types';
import {useNewBillsBemobiFeatureFlag} from '../useNewBillsBemobiFeatureFlag';
import {ServicesTitlesEnum} from '../useServicesList/types';
import {makeGetWhoIndicatesLink} from 'main/factories/usecases/private/remote-home/get-who-indicates';
import {useVoiceTranscriptionDrawerStore} from 'presentation/components/VoiceTranscriptionDrawer/store';
import {useCongenereEligibilityStore} from 'presentation/pages/private/store/useCongenereEligibilityStore';

const {
	bookExams,
	bookHealth,
	networkProviderHealth,
	bookOdonto,
	bookTelehealth,
	emergencyTelehealth,
	myAppointments,
	copaymentStatement,
	incomeTaxStatement,
	examsResult,
	authorizationsOdonto,
	authorizationsHealth,
	refund,
	consultRefund,
	serviceChannels,
	consultProtocols,
	refundOdonto,
	concierge,
	myReferrals,
	perksClub,
	recommendation,
	pinSS,
	stayLetter,
	networkProviderOdonto,
	favorites,
} = translations['pt-br'].privateFlow.services;
const {
	accreditedNetwork,
	telemedicine,
	authorizations,
	refund: shortcutRefund,
} = translations['pt-br'].privateFlow.home.portalShortcutLabels;

export function useServicesListForShortcuts(query = '') {
	const makeHealthEmergencyLink = useHealthEmergencyLinkFactory();
	const {
		isGndiSaoPaulo,
		isNotredameSaude,
		dataHostNameState: {product},
	} = useHostName();
	const {
		flPlanoSomenteHospitalar,
		businessPlan,
		healthOnly,
		odontoOnly,
		healthOdonto,
		flAdministradora,
		flPad,
		isHolder,
		accesses,
		isResponsibleWithoutBenefits,
		isB2BAccess,
		isEarlyAccess,
	} = useAccountInformation();

	const {
		isValid,
		isContractSuspended,
		isCancelledLessThanThreeMonths,
		isCancelledAtOneYear,
		isCancelledAtTwoYears,
		isCancelledAtFiveYears,
		isCancelledAtOneYearAndThreeMonths,
	} = useContractStatus();

	const {congenereEligibility} = useCongenereEligibilityStore();

	const {isEnabled: isIrDemonstrativeFeatureFlagEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_INCOME_TAX,
	);

	const {isEnabled: isAuthorizationOdontoFeatureFlagEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_623_ODONTO,
	);

	const {isEnabled: isMyReferralsFeatureFlagEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_MEUS_ENCAMINHAMENTOS,
	);

	const {isEnabled: newBillsFlagIsEnabled} = useNewBillsBemobiFeatureFlag();
	const {isEnabled: isUrgencyTeleTermsEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_URGENCY_TELE_TERMS,
	);
	const {isEnabled: isLandingPageTeleEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_LANDING_PAGE_TELE,
	);

	const {isEnabled: isFavoriteDoctorsEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_FAVORITE_DOCTORS,
	);

	const {showConciergeButton, onOpenConciergeDrawer} = useConciergeBenefits();

	const onOpenVoiceTranscriptionDrawer = useVoiceTranscriptionDrawerStore(state => state.onOpen);

	const showRefundServiceForShortcut =
		isValid && accesses?.refund && !odontoOnly && (isGndiSaoPaulo || isNotredameSaude);

	const showMyAppointments =
		(isValid && !isResponsibleWithoutBenefits && !(healthOnly && flPlanoSomenteHospitalar)) ||
		(isCancelledLessThanThreeMonths && !isResponsibleWithoutBenefits);

	const showOdontoBookService =
		!isResponsibleWithoutBenefits && !isContractSuspended && (odontoOnly || healthOdonto);

	const showBookService =
		!isResponsibleWithoutBenefits &&
		(healthOdonto || (healthOnly && !flPlanoSomenteHospitalar));

	const showHealthBookServiceForShortcut =
		!isResponsibleWithoutBenefits &&
		!isContractSuspended &&
		((healthOnly && !flPlanoSomenteHospitalar) || (healthOdonto && !flPlanoSomenteHospitalar));

	const showExamsResults =
		!isResponsibleWithoutBenefits &&
		(isValid || isCancelledAtTwoYears) &&
		!odontoOnly &&
		!isB2BAccess &&
		!isEarlyAccess;

	const showRefundOdonto =
		(isCancelledAtOneYear || (isValid && isResponsibleWithoutBenefits)) &&
		isGndiSaoPaulo &&
		!healthOnly &&
		!isEarlyAccess;

	const shouldShowTab = flPad || (!businessPlan && !flAdministradora);

	const showIRBase = shouldShowTab || (businessPlan && accesses.incomeTaxEmploy);

	const showIrDemonstrative = () => {
		if (!showIRBase || isB2BAccess) return false;

		const isFeatureEnabledAndValid = isIrDemonstrativeFeatureFlagEnabled && isValid;
		const isSpecialResponsible = isResponsibleWithoutBenefits || isCancelledAtFiveYears;

		return isFeatureEnabledAndValid || isSpecialResponsible;
	};

	const showHealthAuthorizationsForShortcut =
		!isResponsibleWithoutBenefits && (healthOnly || healthOdonto);

	const showOdontoAuthorizationsForShortcut =
		!isResponsibleWithoutBenefits &&
		isAuthorizationOdontoFeatureFlagEnabled &&
		(odontoOnly || healthOdonto);

	const showMyReferrals =
		isValid &&
		!isContractSuspended &&
		!isResponsibleWithoutBenefits &&
		isMyReferralsFeatureFlagEnabled &&
		(healthOnly || healthOdonto) &&
		!isEarlyAccess;

	const showBookTelehealth =
		isValid &&
		!isContractSuspended &&
		showBookService &&
		!isEarlyAccess &&
		!congenereEligibility;

	const showBookHealth =
		isValid && showHealthBookServiceForShortcut && !isEarlyAccess && !congenereEligibility;

	const showBookExams =
		isValid &&
		!isContractSuspended &&
		showBookService &&
		!isEarlyAccess &&
		!congenereEligibility;
	const showBookOdonto =
		isValid && showOdontoBookService && !isEarlyAccess && !congenereEligibility;

	const showEmergencyTelehealth =
		isValid && showBookService && !isB2BAccess && !isEarlyAccess && !congenereEligibility;

	const showHealthAuthorizations =
		isValid && showHealthAuthorizationsForShortcut && !isEarlyAccess;
	const showOdontoAuthorizations =
		isValid && showOdontoAuthorizationsForShortcut && !isEarlyAccess;

	const showRefund =
		(isCancelledAtOneYearAndThreeMonths &&
			accesses?.refund &&
			!odontoOnly &&
			(isGndiSaoPaulo || isNotredameSaude)) ||
		showRefundServiceForShortcut;

	const showBillet =
		(isValid || isCancelledAtFiveYears) &&
		isResponsibleWithoutBenefits &&
		shouldShowTab &&
		!isB2BAccess &&
		!isEarlyAccess;

	const showCoPaymentsStatement =
		(isValid || isCancelledAtFiveYears) &&
		isResponsibleWithoutBenefits &&
		!isB2BAccess &&
		!isEarlyAccess &&
		!congenereEligibility;

	const showConsultProtocols =
		(isCancelledLessThanThreeMonths && !isB2BAccess) ||
		((isValid || isCancelledAtFiveYears) && isResponsibleWithoutBenefits && !isB2BAccess);

	const showHelp =
		(isCancelledAtOneYear && !isB2BAccess) ||
		((isValid || isCancelledAtOneYear) && isResponsibleWithoutBenefits && !isB2BAccess);

	const showNetworkProviderHealth =
		(isGndiSaoPaulo || isEarlyAccess) &&
		isValid &&
		!isB2BAccess &&
		!isResponsibleWithoutBenefits &&
		!odontoOnly;

	const showNetworkProviderOdonto =
		isEarlyAccess &&
		isValid &&
		!isResponsibleWithoutBenefits &&
		!healthOnly &&
		!congenereEligibility;

	const showBemobiFlow = newBillsFlagIsEnabled
		? ServicesTitlesEnum.INVOICES
		: ServicesTitlesEnum.BILLET;

	const emergencyTelehealthShortcutAction = (() => {
		if (isLandingPageTeleEnabled) {
			return {route: EmergencyTelehealthEnum.EMERGENCY_TELEHEALTH_FLOW};
		}

		if (isUrgencyTeleTermsEnabled) {
			return {
				handleClick: () =>
					onOpenVoiceTranscriptionDrawer(null, TermsOriginEnum.EMERGENCY_TELEHEALTH),
			};
		}

		return {endpoint: makeHealthEmergencyLink};
	})();

	const SERVICES_LIST_FOR_SHORTCUTS: Service[] = [
		{
			title: bookTelehealth,
			icon: 'remember-me-filled',
			route: TelehealthBookingEnum.TELEHEALTH_BOOKING_FLOW,
			isInversedColor: true,
			show: showBookTelehealth,
			type: ServicesTypesEnum.TELEHEALTH,
			dataTagId: AppointmentBookingEnum.CLICK_AGENDAR_TELECONSULTA,
		},
		{
			title: favorites,
			icon: 'heart-filled',
			route: FavoritesEnum.FAVORITE_DOCTORS,
			show: showBookHealth && isFavoriteDoctorsEnabled,
			type: ServicesTypesEnum.HEALTH,
			id: 'favoritesShortcut',
		},
		{
			title: bookHealth,
			icon: 'stethoscope',
			route: HealthBookingEnum.HEALTH_BOOKING_FLOW,
			show: showBookHealth,
			type: ServicesTypesEnum.HEALTH,
			dataTagId: AppointmentBookingEnum.CLICK_AGENDAR_CONSULTA,
		},
		{
			title: emergencyTelehealth,
			icon: 'video-camera-front-filled',
			show: showEmergencyTelehealth,
			type: ServicesTypesEnum.EMERGENCY_TELEHEALTH,
			dataTagId: AppointmentBookingEnum.CLICK_PRONTO_ATENDIMENTO_DIGITAL,
			warningDrawerContent: true,
			...emergencyTelehealthShortcutAction,
		},
		{
			title: networkProviderHealth,
			icon: 'pin-drop-filled',
			route: GuidesEnum.SERVICE_NETWORK_HEALTH,
			show: showNetworkProviderHealth,
			type: ServicesTypesEnum.OTHERS,
			dataTagId: NetworkEnum.CLICK_REDE_ASSISTENCIAL,
		},
		{
			title: networkProviderOdonto,
			icon: 'pin-drop-filled',
			route: GuidesEnum.SERVICE_NETWORK_ODONTO,
			show: showNetworkProviderOdonto,
			type: ServicesTypesEnum.OTHERS,
			dataTagId: ExternalServicesTaggingEnum.CLICK_GUIA_ODONTO,
		},
		{
			title: perksClub,
			icon: 'basket',
			endpoint: makeGetPerksClubLink,
			show: !isResponsibleWithoutBenefits && isEarlyAccess && !congenereEligibility,
			type: ServicesTypesEnum.OTHERS,
			dataTagId: ExternalServicesTaggingEnum.CLICK_CLUBE_DE_VANTAGENS,
		},
		{
			title: recommendation,
			icon: 'add-reaction-filled',
			endpoint: makeGetWhoIndicatesLink,
			show: !isResponsibleWithoutBenefits && isEarlyAccess && !congenereEligibility,
			type: ServicesTypesEnum.OTHERS,
			dataTagId: ExternalServicesTaggingEnum.CLICK_QUEM_INDICA,
		},
		{
			title: bookExams,
			icon: 'needle',
			route: ExamBookingEnum.EXAM_BOOKING_FLOW,
			show: showBookExams,
			type: ServicesTypesEnum.EXAMS,
			dataTagId: AppointmentBookingEnum.CLICK_AGENDAR_EXAME,
		},
		{
			title: bookOdonto,
			icon: 'tooth-outline',
			route: OdontoBookingEnum.ODONTO_BOOKING_FLOW,
			show: showBookOdonto && odontoOnly,
			type: ServicesTypesEnum.ODONTO,
		},
		{
			title: myAppointments,
			icon: 'event-filled',
			route: isValid
				? MyAppointmentsEnum.MY_APPOINTMENTS
				: MyAppointmentsEnum.MY_APPOINTMENTS_HISTORY,
			show: showMyAppointments && !isEarlyAccess,
			type: ServicesTypesEnum.OTHERS,
			dataTagId: AppointmentBookingEnum.CLICK_VER_MEUS_AGENDAMENTOS,
		},
		{
			title: myReferrals,
			icon: 'assignment-outline',
			route: MyReferralsEnum.MY_REFERRALS,
			show: showMyReferrals,
			type: ServicesTypesEnum.OTHERS,
			dataTagId: MyReferralsTaggingEnum.CLICK_ACESSAR_MEUS_ENCAMINHAMENTOS_MENU,
		},
		{
			title: bookOdonto,
			icon: 'tooth-outline',
			route: OdontoBookingEnum.ODONTO_BOOKING_FLOW,
			show: showBookOdonto && healthOdonto,
			type: ServicesTypesEnum.ODONTO,
		},
		{
			title: incomeTaxStatement,
			icon: 'lion-filled',
			route: MyPaymentsEnum.INCOME_TAX,
			show: showIrDemonstrative() && !isResponsibleWithoutBenefits && !isEarlyAccess,
			type: ServicesTypesEnum.OTHERS,
			dataTagId: ServicesTaggingEnum.CLICK_DEMOSTRATIVO_IR,
		},
		{
			title: examsResult,
			icon: 'clipboard-pulse-outline',
			show: showExamsResults,
			featureFlag: EXAMS_FEATURE_BY_HOST[product],
			route: ExamResultsEnum.EXAM_RESULTS,
			endpoint: makeGetExamResultsUrl,
			type: ServicesTypesEnum.OTHERS,
			dataTagId: ServicesTaggingEnum.CLICK_RESULTADO_DE_EXAMES,
		},
		{
			title: authorizationsHealth,
			icon: 'document-heart-outlined',
			show: showHealthAuthorizations,
			route: AuthorizationsEnum.AUTHORIZATIONS,
			type: ServicesTypesEnum.AUTHORIZATIONS,
			dataTagId: AutoSolEnum.CLICK_ACESSAR_AUTOSOL_SAUDE,
		},
		{
			title: authorizationsOdonto,
			icon: 'document-tooth-outlined',
			show: showOdontoAuthorizations,
			route: AuthorizationsEnum.AUTHORIZATIONS_ODONTO,
			type: ServicesTypesEnum.AUTHORIZATIONS,
		},
		{
			title: isHolder ? refund : consultRefund,
			icon: 'currency-exchange-filled',
			route: RefundEnum.REFUND,
			show: showRefund && !isEarlyAccess,
			type: ServicesTypesEnum.OTHERS,
			dataTagId: isHolder ? RefundTaggingEnum.CLICK_ACESSAR_REEMBOLSO_SAUDE : undefined,
		},
		{
			title: refundOdonto,
			icon: 'odonto-exchange-filled',
			endpoint: makeGetRefundOdonto,
			show: showRefundOdonto,
			type: ServicesTypesEnum.OTHERS,
		},
		{
			title: concierge,
			icon: 'bell',
			show: showConciergeButton && !isEarlyAccess,
			type: ServicesTypesEnum.OTHERS,
			handleClick: onOpenConciergeDrawer,
			dataTagId: ServicesTaggingEnum.CLICK_CONCIERGE,
		},
		{
			title: showBemobiFlow,
			icon: 'receipt-long-filled',
			route: MyPaymentsEnum.OPEN_BILLS,
			show: showBillet,
			type: ServicesTypesEnum.OTHERS,
		},
		{
			title: copaymentStatement,
			icon: 'text-box-outline',
			route: MyPaymentsEnum.COPARTICIPATION,
			show: showCoPaymentsStatement,
			type: ServicesTypesEnum.OTHERS,
		},
		{
			title: incomeTaxStatement,
			icon: 'lion-filled',
			route: MyPaymentsEnum.INCOME_TAX,
			show: showIrDemonstrative() && isResponsibleWithoutBenefits && !isEarlyAccess,
			type: ServicesTypesEnum.OTHERS,
			dataTagId: ServicesTaggingEnum.CLICK_DEMOSTRATIVO_IR,
		},
		{
			title: consultProtocols,
			icon: 'content-paste-search-filled',
			route: ConsultProtocolsEnum.CONSULT_PROTOCOLS,
			show: showConsultProtocols || isEarlyAccess,
			type: ServicesTypesEnum.OTHERS,
		},
		{
			title: pinSS,
			icon: 'keyboard-filled',
			endpoint: makeGetPinSsUrl,
			show: !isResponsibleWithoutBenefits && isEarlyAccess && !congenereEligibility,
			type: ServicesTypesEnum.OTHERS,
			dataTagId: ExternalServicesTaggingEnum.CLICK_PIN_SS,
		},
		{
			title: stayLetter,
			icon: 'mark-as-read',
			endpoint: makeGetStayLetterUrl,
			show: !isResponsibleWithoutBenefits && isEarlyAccess && !congenereEligibility,
			type: ServicesTypesEnum.OTHERS,
			dataTagId: ExternalServicesTaggingEnum.CLICK_CARTA_PERMANENCIA,
			isBlob: true,
		},
		{
			title: serviceChannels,
			icon: 'help-filled',
			route: HelpCenterEnum.HELP,
			show: showHelp || isEarlyAccess,
			type: ServicesTypesEnum.OTHERS,
		},
	];

	const NOTREDAME_SHORTCUTS_LIST: Service[] = [
		{
			title: accreditedNetwork,
			icon: 'pin-drop-filled',
			isInversedColor: true,
			show: showBookTelehealth,
			type: ServicesTypesEnum.TELEHEALTH,
			dataTagId: AppointmentBookingEnum.CLICK_AGENDAR_TELECONSULTA,
		},
		{
			title: telemedicine,
			icon: 'video-default',
			route: TelehealthBookingEnum.TELEHEALTH_BOOKING_FLOW,
			show: showBookTelehealth,
			type: ServicesTypesEnum.TELEHEALTH,
			dataTagId: AppointmentBookingEnum.CLICK_AGENDAR_TELECONSULTA,
		},
		{
			title: authorizations,
			icon: 'document-heart-outlined',
			show: showHealthAuthorizations,
			route: AuthorizationsEnum.AUTHORIZATIONS,
			type: ServicesTypesEnum.AUTHORIZATIONS,
		},
		{
			title: shortcutRefund,
			icon: 'currency-exchange-filled',
			show: showRefund && !isEarlyAccess,
			route: RefundEnum.REFUND,
			type: ServicesTypesEnum.OTHERS,
		},
	];

	const SHORTCUTS_LIST = (
		isNotredameSaude ? NOTREDAME_SHORTCUTS_LIST : SERVICES_LIST_FOR_SHORTCUTS
	).filter(service => service?.show && findItemByQuery({item: service?.title, query}));

	const isShortcutsListEmpty = SHORTCUTS_LIST?.length === 0;
	return {
		SHORTCUTS_LIST,
		isShortcutsListEmpty,
	};
}
